import React from 'react'

import {
  BodyText,
  Button,
  CheckBoxInput,
  FieldController,
  Form,
  HeadingXSmall,
  Modal,
  ModalButtons,
  SubmitButton,
} from '@pb/components'
import { getLabelFromStorage, getLocale } from '@pb/utils'
import { useExperiment, useFeature } from '@optimizely/react-sdk'
import { useDispatch, useSelector } from 'react-redux'

import optimizelyConstants from 'constants/optimizely'
import { trackOptimizelyEvent } from 'helpers/optimizely'
import { RadioButton, RadioButtonList } from 'ui/RadioButtonList'

import { CRMEN_ANSWERED_DONATE_VOUCHER } from '../../constants'
import actions from '../../duck/actions'
import selectors from '../../duck/selectors'
import styles from './DonateVoucherModal.scss'

const radioButtonsId = 'donateVocuherRadioButton'

const DonateVoucherModal: React.FC = () => {
  const dispatch = useDispatch()
  const isModalOpen = useSelector(selectors.getIsDonateVoucherModalOpen)
  const [isDonateVoucherEnabled, variables] = useFeature(optimizelyConstants.DONATE_VOUCHER)
  const [donateVoucherVariation] = useExperiment(optimizelyConstants.DONATE_VOUCHER_EXPERIMENT)
  const showDonateVoucher = isDonateVoucherEnabled && donateVoucherVariation === 'on'

  if (!showDonateVoucher) {
    return null
  }
  const closeModal = (): any => dispatch(actions.closeDonateVoucherModal())

  const { translations } = variables as any
  const localeTranslation = translations[getLocale('lowerCase')]
  const handleSubmit = (values): void => {
    Object.keys(values).forEach((value) => {
      if (value === radioButtonsId && !!values[value]) {
        trackOptimizelyEvent(values[value])
      }
      if (value !== radioButtonsId && !!values[value]) {
        trackOptimizelyEvent(value)
      }
    })
    trackOptimizelyEvent(optimizelyConstants.CLICK_SURVEY_SUBMIT)
    localStorage.setItem(CRMEN_ANSWERED_DONATE_VOUCHER, 'true')
    window.location.reload()
  }

  const handleCancel = (): void => {
    trackOptimizelyEvent(optimizelyConstants.CLICK_SURVEY_CANCEL)
    closeModal()
  }

  const handleClose = (): void => {
    trackOptimizelyEvent(optimizelyConstants.CLICK_SURVEY_CLOSE)
    closeModal()
  }
  const { radioButtonAnswers, checkboxAnswers } = localeTranslation

  return (
    <Modal
      closeButtonText={getLabelFromStorage('general.close')}
      isOpen={isModalOpen}
      toggleModal={handleClose}
      title={localeTranslation.questionnaireTitle}
      modalHeadingClassName={styles.modalTitle}
    >
      <BodyText className={styles.questionnaireInfoText}>
        {localeTranslation.questionnaireInfoText}
      </BodyText>
      <Form onSubmit={handleSubmit}>
        <HeadingXSmall Tag="p" left>
          {localeTranslation.question1}
          <BodyText Tag="span">{localeTranslation.question1SufixText}</BodyText>
        </HeadingXSmall>
        <RadioButtonList
          className={styles.radioButtonList}
          name={radioButtonsId}
          rules={{ required: 'Required' }}
        >
          {radioButtonAnswers.map(({ value, text }) => (
            <RadioButton
              className={styles.radioButton}
              key={value}
              name={radioButtonsId}
              inputValue={value}
              required
            >
              <BodyText>{text}</BodyText>
            </RadioButton>
          ))}
        </RadioButtonList>

        <HeadingXSmall Tag="p" left>
          {localeTranslation.question2}
        </HeadingXSmall>
        {checkboxAnswers.map(({ value: checkboxValue, text }) => (
          <FieldController
            key={checkboxValue}
            name={checkboxValue}
            type="checkbox"
            defaultValue={false}
            render={({ value, ...rest }) => (
              <CheckBoxInput
                checked={value}
                {...rest}
                shouldShowValidationIcon={false}
                label={<BodyText left>{text}</BodyText>}
              />
            )}
          />
        ))}
        <ModalButtons bottom fullWidth className={styles.buttonGroup}>
          <SubmitButton fullWidth>{localeTranslation.submit}</SubmitButton>
          <Button secondary fullWidth onClick={handleCancel}>
            {localeTranslation.cancel}
          </Button>
        </ModalButtons>
      </Form>
    </Modal>
  )
}

export default DonateVoucherModal
